
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { 
  Search, 
  Filter,
  X,
  Calendar,
  Euro,
  TrendingUp
} from 'lucide-react';
import { AdminSubmission } from '@/hooks/useAdminData';

interface FilterState {
  searchTerm: string;
  businessType: string;
  scoreRange: [number, number];
  costRange: [number, number];
  dateRange: string;
  expansionPlans: string[];
}

interface AdminFiltersProps {
  submissions: AdminSubmission[];
  onFilterChange: (filteredSubmissions: AdminSubmission[]) => void;
}

const AdminFilters = ({ submissions, onFilterChange }: AdminFiltersProps) => {
  const [filters, setFilters] = React.useState<FilterState>({
    searchTerm: '',
    businessType: 'all',
    scoreRange: [0, 100],
    costRange: [0, 10000],
    dateRange: 'all',
    expansionPlans: []
  });

  const [isFiltersExpanded, setIsFiltersExpanded] = React.useState(false);

  // Get unique business types and expansion plans
  const uniqueBusinessTypes = React.useMemo(() => 
    [...new Set(submissions.map(s => s.business_type))], [submissions]
  );

  const uniqueExpansionPlans = React.useMemo(() => {
    const plans = new Set<string>();
    submissions.forEach(s => s.expansion_plans.forEach(plan => plans.add(plan)));
    return Array.from(plans);
  }, [submissions]);

  // Apply filters
  React.useEffect(() => {
    let filtered = submissions;

    // Search filter
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(sub => 
        sub.business_type.toLowerCase().includes(searchLower) ||
        sub.company_name?.toLowerCase().includes(searchLower) ||
        sub.email?.toLowerCase().includes(searchLower)
      );
    }

    // Business type filter
    if (filters.businessType !== 'all') {
      filtered = filtered.filter(sub => sub.business_type === filters.businessType);
    }

    // Score range filter
    filtered = filtered.filter(sub => {
      const score = sub.energy_potential_score || 0;
      return score >= filters.scoreRange[0] && score <= filters.scoreRange[1];
    });

    // Cost range filter
    filtered = filtered.filter(sub => 
      sub.monthly_energy_costs >= filters.costRange[0] && 
      sub.monthly_energy_costs <= filters.costRange[1]
    );

    // Date range filter
    if (filters.dateRange !== 'all') {
      const now = new Date();
      const cutoffDate = new Date();
      
      switch (filters.dateRange) {
        case 'week':
          cutoffDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          cutoffDate.setMonth(now.getMonth() - 1);
          break;
        case '3months':
          cutoffDate.setMonth(now.getMonth() - 3);
          break;
        case '6months':
          cutoffDate.setMonth(now.getMonth() - 6);
          break;
      }
      
      filtered = filtered.filter(sub => new Date(sub.created_at) >= cutoffDate);
    }

    // Expansion plans filter
    if (filters.expansionPlans.length > 0) {
      filtered = filtered.filter(sub => 
        filters.expansionPlans.some(plan => sub.expansion_plans.includes(plan))
      );
    }

    onFilterChange(filtered);
  }, [filters, submissions, onFilterChange]);

  const clearFilters = () => {
    setFilters({
      searchTerm: '',
      businessType: 'all',
      scoreRange: [0, 100],
      costRange: [0, 10000],
      dateRange: 'all',
      expansionPlans: []
    });
  };

  const activeFiltersCount = React.useMemo(() => {
    let count = 0;
    if (filters.searchTerm) count++;
    if (filters.businessType !== 'all') count++;
    if (filters.scoreRange[0] > 0 || filters.scoreRange[1] < 100) count++;
    if (filters.costRange[0] > 0 || filters.costRange[1] < 10000) count++;
    if (filters.dateRange !== 'all') count++;
    if (filters.expansionPlans.length > 0) count++;
    return count;
  }, [filters]);

  const toggleExpansionPlan = (plan: string) => {
    setFilters(prev => ({
      ...prev,
      expansionPlans: prev.expansionPlans.includes(plan)
        ? prev.expansionPlans.filter(p => p !== plan)
        : [...prev.expansionPlans, plan]
    }));
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters & Zoeken
            {activeFiltersCount > 0 && (
              <Badge variant="secondary">{activeFiltersCount}</Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            {activeFiltersCount > 0 && (
              <Button variant="outline" size="sm" onClick={clearFilters}>
                <X className="h-4 w-4 mr-1" />
                Reset
              </Button>
            )}
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
            >
              {isFiltersExpanded ? 'Inklappen' : 'Uitklappen'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Search */}
        <div className="flex items-center space-x-2">
          <Search className="h-4 w-4 text-gray-400" />
          <Input
            placeholder="Zoek op bedrijfsnaam, email of bedrijfstype..."
            value={filters.searchTerm}
            onChange={(e) => setFilters(prev => ({ ...prev, searchTerm: e.target.value }))}
            className="flex-1"
          />
        </div>

        {isFiltersExpanded && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-4 border-t">
            {/* Business Type */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Bedrijfstype</label>
              <Select value={filters.businessType} onValueChange={(value) => 
                setFilters(prev => ({ ...prev, businessType: value }))
              }>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle types</SelectItem>
                  {uniqueBusinessTypes.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date Range */}
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                Periode
              </label>
              <Select value={filters.dateRange} onValueChange={(value) => 
                setFilters(prev => ({ ...prev, dateRange: value }))
              }>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle tijd</SelectItem>
                  <SelectItem value="week">Afgelopen week</SelectItem>
                  <SelectItem value="month">Afgelopen maand</SelectItem>
                  <SelectItem value="3months">Afgelopen 3 maanden</SelectItem>
                  <SelectItem value="6months">Afgelopen 6 maanden</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Energy Score Range */}
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-1">
                <TrendingUp className="h-4 w-4" />
                Energy Score: {filters.scoreRange[0]}% - {filters.scoreRange[1]}%
              </label>
              <Slider
                value={filters.scoreRange}
                onValueChange={(value) => setFilters(prev => ({ ...prev, scoreRange: value as [number, number] }))}
                max={100}
                min={0}
                step={5}
                className="w-full"
              />
            </div>

            {/* Cost Range */}
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-1">
                <Euro className="h-4 w-4" />
                Energiekosten: €{filters.costRange[0]} - €{filters.costRange[1]}
              </label>
              <Slider
                value={filters.costRange}
                onValueChange={(value) => setFilters(prev => ({ ...prev, costRange: value as [number, number] }))}
                max={10000}
                min={0}
                step={100}
                className="w-full"
              />
            </div>

            {/* Expansion Plans */}
            <div className="space-y-2 md:col-span-2">
              <label className="text-sm font-medium">Uitbreidingsplannen</label>
              <div className="flex flex-wrap gap-2">
                {uniqueExpansionPlans.map(plan => (
                  <Badge
                    key={plan}
                    variant={filters.expansionPlans.includes(plan) ? "default" : "outline"}
                    className="cursor-pointer hover:bg-primary/10"
                    onClick={() => toggleExpansionPlan(plan)}
                  >
                    {plan}
                    {filters.expansionPlans.includes(plan) && (
                      <X className="h-3 w-3 ml-1" />
                    )}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminFilters;
